import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import DOMPurify from 'dompurify';
import fetchWrapper from '@/utils/fetch';
import { captureException } from '@/dashboard-react/utils/sentry';
import { readLastLoginMethod, rememberLoginMethod } from '@/utils/lastLoginMethod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DevKitFoot, DevKitPanel, devKitControlClass } from '@/components/devKit/DevKitPanel';
import AuthCard from './AuthCard';
import AuthDivider from './AuthDivider';
import SocialButtons from './SocialButtons';
import TextField from './TextField';
import { resolveRedirectTarget } from './redirect';
import type { LoginCardProps } from './types';

const LOCKOUT_MESSAGE = 'Too many incorrect login attempts. Please wait 5 minutes and try again.';

const EXPECTED_FAILURE_STATUSES = new Set([401, 429]);
const GENERIC_FAILURE = 'We could not log you in. Please try again.';

const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Please enter your email address.')
    .refine((value) => z.email().safeParse(value).success, 'Please enter a valid email address.'),
  password: z.string().min(1, 'Please enter your password.'),
  rememberMe: z.boolean(),
  // Threaded through form state (never a visible field) rather than a ref, so
  // onValid reads it from its `values` argument instead of a ref during
  // render. Set by the dev-only one-click login; see handleDevLogin below.
  devLogin: z.boolean(),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const sanitizeError = (message: string) =>
  DOMPurify.sanitize(message, {
    ALLOWED_TAGS: ['a', 'strong', 'em', 'b', 'i', 'br'],
    // No `target`: error links have no reason to open a new tab, and DOMPurify
    // can't force the `rel="noopener"` that a `target="_blank"` would require.
    ALLOWED_ATTR: ['href', 'rel'],
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });

const buildLoginError = async (response: Response, forgotPasswordPath: string): Promise<string> => {
  if (response.status === 429) return LOCKOUT_MESSAGE;

  const body = await response.text().catch(() => '');
  let message: string;
  // The server flags failures a password reset can't fix (provider-only,
  // blocked-domain, locked and deactivated accounts) with
  // `password_reset_available: false`. Gate the prompt on that real signal
  // rather than substring-matching the copy.
  let resetAvailable = true;
  try {
    const parsed = JSON.parse(body) as { full_error?: string; error?: string; password_reset_available?: boolean };
    message = parsed.full_error || parsed.error || GENERIC_FAILURE;
    if (parsed.password_reset_available === false) resetAvailable = false;
  } catch {
    // A non-JSON body is usually a proxy or gateway error page, so only trust it
    // when it looks like a bare message rather than a document.
    const text = body.trim();
    message = text.length > 0 && text.length < 200 && !text.includes('<') ? text : GENERIC_FAILURE;
  }

  if (resetAvailable) {
    message += `, or <a href="${forgotPasswordPath}">reset your password</a>.`;
  }
  return message;
};

export function LoginCard({
  signUpPath,
  sessionPath,
  githubPath,
  googlePath,
  ssoPath,
  forgotPasswordPath,
  afterLoginPath,
  knownEmail,
  returnTo,
  unauthenticatedMessage,
  provider,
  devLoginAccounts,
  devLoginPassword,
}: LoginCardProps) {
  const [error, setError] = useState<string | null>(unauthenticatedMessage ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [lastUsedMethod] = useState(() => readLastLoginMethod());
  const formRef = useRef<HTMLFormElement>(null);
  const devLoginPending = useRef(false);
  const [devLoginRequest, setDevLoginRequest] = useState(0);
  const [devLoginAccount, setDevLoginAccount] = useState(() => devLoginAccounts?.[0] ?? '');

  const { control, handleSubmit, reset, setValue } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onTouched',
    defaultValues: { email: knownEmail ?? '', password: '', rememberMe: false, devLogin: false },
  });

  // Fills the form with a seeded account and requests a submit once React
  // has committed the reset.
  const handleDevLogin = useCallback(
    (email: string) => {
      reset({ email, password: devLoginPassword ?? '', rememberMe: false, devLogin: true });
      setError(null);
      devLoginPending.current = true;
      setDevLoginRequest((previous) => previous + 1);
    },
    [reset, devLoginPassword],
  );

  useEffect(() => {
    if (!devLoginPending.current || submitting) return;

    devLoginPending.current = false;
    formRef.current?.requestSubmit();
  }, [devLoginRequest, submitting]);

  const handleDevLoginChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => setDevLoginAccount(event.target.value),
    [],
  );

  const handleDevLoginClick = useCallback(() => handleDevLogin(devLoginAccount), [handleDevLogin, devLoginAccount]);

  const onValid = useCallback(
    async (values: LoginFormValues) => {
      setSubmitting(true);
      setError(null);
      rememberLoginMethod('email');
      // Reset immediately so a later retry after a failure doesn't resend it.
      if (values.devLogin) setValue('devLogin', false);

      try {
        const response = await fetchWrapper(sessionPath, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            user: { email: values.email, password: values.password, remember_me: values.rememberMe ? '1' : '0' },
            return_to: returnTo,
            // Signals the server (after_sign_in_path_for) to ignore any stale
            // stored location, so one-click login always lands on the
            // tester's own dashboard.
            ...(values.devLogin ? { dev_login: '1' } : {}),
          }),
        });

        if (response.ok) {
          // 2FA returns 200 (not a redirect) with the Authy path in a
          // `{ location }` body and header; a plain login carries it in the
          // Location header. Honour it or the user is sent to the dashboard
          // while only half signed in and bounced back to login.
          const body = (await response.json().catch(() => null)) as { location?: string } | null;
          window.location.assign(resolveRedirectTarget(response, body, returnTo || afterLoginPath));
          return;
        }

        if (!EXPECTED_FAILURE_STATUSES.has(response.status)) {
          captureException(new Error(`Login request failed with ${response.status}`), 'LoginCard', 'submit', {
            status: response.status,
          });
        }

        setError(await buildLoginError(response, forgotPasswordPath));
        setSubmitting(false);
      } catch (caught) {
        captureException(caught, 'LoginCard', 'submit');
        setError('We could not reach the server. Please try again.');
        setSubmitting(false);
      }
    },
    [sessionPath, afterLoginPath, forgotPasswordPath, returnTo, setValue],
  );

  const fieldErrors = (
    fieldState: { error?: { message?: string }; isTouched: boolean },
    isSubmitted: boolean,
  ): string[] | undefined =>
    (fieldState.isTouched || isSubmitted) && fieldState.error ? [fieldState.error.message ?? ''] : undefined;

  const submit = handleSubmit(onValid);

  return (
    <>
      <AuthCard
        title="Log in to Ably"
        cardId="login-box"
        headerAction={
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-neutral-800 sm:inline">Don&rsquo;t have an account?</span>
            <a
              href={signUpPath}
              className="focus-base rounded-md border border-neutral-300 px-4 py-2 text-xs font-bold tracking-wide text-neutral-1300 transition-colors hover:bg-neutral-100 hover:text-neutral-1300 active:bg-neutral-200"
            >
              Sign up
            </a>
          </div>
        }
      >
        <div className="flex flex-col gap-6">
          {error ? (
            <div
              role="alert"
              className="rounded-md bg-orange-100 p-3 text-xs text-orange-900 [&_a]:font-semibold [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeError(error) }}
            />
          ) : null}

          <SocialButtons
            githubPath={githubPath}
            googlePath={googlePath}
            ssoPath={ssoPath}
            lastUsedMethod={lastUsedMethod}
            authAction="login"
            hiddenProvider={provider}
          />

          <AuthDivider lastUsed={lastUsedMethod === 'email'}>
            <span>Or continue with email</span>
          </AuthDivider>

          <form
            id="new_session"
            ref={formRef}
            onSubmit={submit}
            noValidate
            data-testid="login-form"
            className="flex flex-col gap-4"
          >
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState, formState }) => (
                <TextField
                  label="Email"
                  id="new_session_user_email"
                  name="user[email]"
                  type="email"
                  autoComplete="email"
                  required
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errors={fieldErrors(fieldState, formState.isSubmitted)}
                />
              )}
            />

            <Controller
              control={control}
              name="password"
              render={({ field, fieldState, formState }) => (
                <TextField
                  label="Password"
                  id="new_session_user_password"
                  name="user[password]"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errors={fieldErrors(fieldState, formState.isSubmitted)}
                  labelAction={
                    <a
                      href={forgotPasswordPath}
                      className="focus-base rounded-md text-sm font-medium text-gui-default transition-colors hover:text-gui-hover active:text-gui-active hover:underline"
                    >
                      Forgot password?
                    </a>
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="rememberMe"
              render={({ field }) => (
                <div className="flex w-fit items-center gap-2">
                  <Checkbox
                    id="remember-me"
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    onBlur={field.onBlur}
                    className="size-5 rounded border-neutral-300 shadow-none data-[state=checked]:border-orange-600 data-[state=checked]:bg-orange-600 data-[state=checked]:text-neutral-000"
                  />
                  <label htmlFor="remember-me" className="cursor-pointer text-xs text-neutral-1000">
                    Stay signed in for 2 weeks
                  </label>
                </div>
              )}
            />

            <button
              type="submit"
              disabled={submitting}
              className="focus-base flex h-14 w-full items-center justify-center rounded-md border border-neutral-300 bg-neutral-000 text-sm font-semibold text-neutral-1300 transition-colors hover:bg-neutral-100 active:bg-neutral-200 disabled:pointer-events-none disabled:opacity-50"
            >
              {submitting ? 'Please wait...' : 'Log in'}
            </button>
          </form>
        </div>
      </AuthCard>

      {/* Dev/review-only one-click login for seeded accounts, at page root so it
          never affects the card's layout. There is deliberately no session-creation
          endpoint here: selecting an account fills and submits the normal login
          form, so lockout, 2FA and audit hooks all still apply. */}
      {devLoginAccounts?.length ? (
        <DevKitPanel title="One-click login" action="Sign in">
          <select
            aria-label="Seeded account to sign in as"
            value={devLoginAccount}
            onChange={handleDevLoginChange}
            className={devKitControlClass}
          >
            {devLoginAccounts.map((email) => (
              <option key={email} value={email}>
                {email}
              </option>
            ))}
          </select>

          <Button type="button" variant="primary" onClick={handleDevLoginClick} className="w-full">
            Sign in
          </Button>

          <DevKitFoot>
            Password <code>{devLoginPassword}</code>
          </DevKitFoot>
        </DevKitPanel>
      ) : null}
    </>
  );
}

export default LoginCard;
